import { Observable } from 'rxjs';
import { SubmittalJobMatchCalculationTypes } from 'src/app/recruiting/candidates/submittal/models/submittal-job-match-calculation-types.model';
import {
    SubmittalJobQualificationRequirement,
    AlternativeCertification
} from 'src/app/recruiting/candidates/submittal/models/submittal-job-qualification-requirement.model';
import { WorkHistoryGapOverlapEdit } from 'src/app/recruiting/candidates/submittal/models/work-history-gap-overlap-edit.model';
import { isNullOrUndefined } from 'src/app/shared/utilities/obj-utilities';

export interface MatchBreakdown {
    percentageMatch?: number;
    candidateId: number;
    unitFormDescriptionId: string;
    unitName: string;
    facilityId: number;
    facilityName: string;
    userCompletedAssessment: boolean;
    jobTrackId: number;
    jobTrackName: string;
    unitTrackId: number;
    unitTrackName: string;
    calculationType: SubmittalJobMatchCalculationTypes;
    hasJobLevelEdits: boolean;
    requiredQualifications: MatchBreakdownGrid[];
    matchCategory?: string;
    matchColor?: string;
}

export interface MatchBreakdownGrid {
    title: string;
    header: string;
    warning?: boolean;
    subtitle?: string;
    otherAssessmentNote?: string;
    requirementHeader?: string;
    isMatchedHeader?: string;
    isOther?: boolean;
    isSkills?: boolean;
    userCompletedAssessment?: boolean;
    candidateId: number;
    jobId?: number;
    requirements$: Observable<{ data: MatchBreakdownRequirement[]; total: number }>;
    hasAdditionalRequirements?: boolean;
}

export class MatchBreakdownRequirement {
    id: number;
    name: string;
    candidateHasMetRequirement: boolean;
    isRequirementExpired: boolean;
    isRequirementMissing: boolean;
    isCandidateExpirationDateMissing: boolean;
    isCandidateValueLowRating: boolean;
    impactedJobs: Record<number, string>;
    explanation?: string;
    workHistoryGap?: WorkHistoryGapOverlapEdit;
    isOther: boolean;
    isMatched: boolean;
    note?: string;
    otherNotes?: string;
    matchedOnOtherAssessment?: string;
    categoryName?: string;
    jobsWithAddedRequirements: number[];
    isAdditionalRequirement: boolean = false;
    availableAlternativesByJobId?: Record<number, AlternativeCertification[]>;
    private readonly hasMetAlternativeCert: boolean = false;
    private readonly jobId?: number;

    constructor(
        public requirement: SubmittalJobQualificationRequirement,
        isAltCertsEnabled: boolean = false,
        jobId?: number
    ) {
        this.id = requirement.id;
        this.name = requirement.name;
        this.candidateHasMetRequirement = requirement.candidateHasMetRequirement;
        this.isOther = requirement.isOther;
        this.impactedJobs = requirement.impactedJobs;
        this.explanation = requirement.explanation;
        this.workHistoryGap = requirement.workHistoryGap;
        this.note = requirement.note;
        this.isRequirementExpired = requirement.isRequirementExpired;
        this.isRequirementMissing = requirement.isRequirementMissing;
        this.isCandidateExpirationDateMissing = requirement.isCandidateExpirationDateMissing;
        this.isCandidateValueLowRating = requirement.isCandidateValueLowRating;
        this.matchedOnOtherAssessment = requirement.matchedOnOtherAssessment;
        this.categoryName = requirement.categoryName;
        this.otherNotes = requirement.otherNotes;
        this.jobsWithAddedRequirements = requirement.jobsWithAddedRequirements ?? [];
        this.jobId = jobId;
        if (isAltCertsEnabled) {
            this.availableAlternativesByJobId = requirement.availableAlternativesByJobId;
            this.hasMetAlternativeCert = this.getHasMetAlternativeCert();
            this.isMatched = this.candidateHasMetRequirement || this.hasMetAlternativeCert;
        } else {
            this.isMatched = this.candidateHasMetRequirement;
        }
    }

    get displayText(): string {
        if (this.isOther) {
            return 'Unable to validate this requirement';
        }

        if (this.isMatched) {
            return 'Yes';
        }

        if (this.isMissing) {
            return 'No, missing';
        }

        if (this.isExpired) {
            return 'No, expired';
        }

        if (this.isExpirationMissing) {
            return 'No, expiration date missing';
        }

        if (this.isExpiredAndExpirationMissing) {
            return 'No, expired/missing expiration date';
        }

        if (this.isLowRate) {
            return 'No, low rating';
        }

        // default for work history when none of the above is set
        if (!this.isMatched) {
            return 'No';
        }

        return '';
    }

    get isExpired() {
        return this.isRequirementExpired && !this.isCandidateExpirationDateMissing;
    }

    get isExpirationMissing() {
        return !this.isRequirementExpired && this.isCandidateExpirationDateMissing;
    }

    get isExpiredAndExpirationMissing() {
        return this.isRequirementExpired && this.isCandidateExpirationDateMissing;
    }

    get isMatchedClass(): string {
        if (this.isMatched) {
            return 'txt-green';
        }
        if (this.isOther) {
            return 'txt-gray';
        }
        return 'txt-red';
    }

    get isLowRate() {
        return this.isCandidateValueLowRating;
    }

    get otherAssessment() {
        return this.isMatched && this.matchedOnOtherAssessment ? `(${this.matchedOnOtherAssessment} assessment)` : '';
    }

    get isMissing() {
        return this.isRequirementMissing;
    }

    private getHasMetAlternativeCert(): boolean {
        if (!this.availableAlternativesByJobId) {
            return false;
        }

        if (isNullOrUndefined(this.jobId)) {
            return Object.values(this.availableAlternativesByJobId).some((alternatives) =>
                (alternatives ?? []).some((alternative) => alternative.isMet)
            );
        }

        const alternatives = this.availableAlternativesByJobId[this.jobId] ?? [];
        return alternatives.some((alternative) => alternative.isMet);
    }

    get isUnableToValidate(): boolean {
        return this.isOther;
    }
}
